// src/components/pages/DailyChangeTracker.js
import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, BarChart3, Play, Square, RefreshCw, Search, Filter, Download } from 'lucide-react';

const LoadingProgress = ({ isLoading, progress = 0, message = "Loading..." }) => {
  if (!isLoading) return null;

  return (
    <div style={{
      padding: '60px 20px',
      textAlign: 'center'
    }}>
      <div style={{
        fontSize: '48px',
        marginBottom: '24px',
        opacity: 0.7,
        animation: 'pulse 2s infinite'
      }}>
        📈
      </div>
      
      <h3 style={{
        fontSize: '18px',
        marginBottom: '16px',
        color: 'var(--text-primary)'
      }}>
        {message}
      </h3>
      
      {/* Progress Bar Container */}
      <div style={{
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto 16px',
        background: 'var(--tertiary-dark)',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid var(--border-color)'
      }}>
        <div style={{
          height: '8px',
          background: 'linear-gradient(90deg, var(--accent-gold), var(--accent-green))',
          width: `${progress}%`,
          transition: 'width 0.3s ease-out',
          borderRadius: '8px'
        }} />
      </div>
      
      {/* Progress Text */}
      <div style={{
        fontSize: '14px',
        color: 'var(--text-secondary)',
        marginBottom: '8px'
      }}>
        {progress}% Complete
      </div>
      
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const DailyChangeTracker = ({ dailyTracker, showToast }) => {
  const [config, setConfig] = useState({
    daysToFetch: 5,
    minVolumeFilter: 1000000,
    sortBy: 'symbol'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [changeFilter, setChangeFilter] = useState('all'); // all, positive, negative, volatile
  const [sortBy, setSortBy] = useState('avgChange');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Daily breakdown sorting
  const [dailySortBy, setDailySortBy] = useState('symbol'); // symbol or date (YYYY-MM-DD)
  const [dailySortDirection, setDailySortDirection] = useState('asc');

  // Sync with dailyTracker config
  useEffect(() => {
    if (dailyTracker.config) {
      setConfig(dailyTracker.config);
    }
  }, [dailyTracker.config]);

  const handleConfigChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    dailyTracker.updateConfig?.(newConfig);
  };

  const handleFetchData = async () => {
    try {
      await dailyTracker.fetchAllData();
      showToast('success', 'Daily Data', `Fetched data for ${dailyTracker.marketData.length} symbols`);
    } catch (error) {
      showToast('error', 'Fetch Failed', error.message);
    }
  };

  const handleCancelFetch = () => {
    dailyTracker.cancelFetch();
    showToast('info', 'Cancelled', 'Data fetch cancelled');
  };

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '--';
    const formatted = value.toFixed(2);
    return value > 0 ? `+${formatted}%` : `${formatted}%`;
  };

  // Filter and sort data
  const filteredData = dailyTracker.marketData?.filter(item => {
    const matchesSearch = item.symbol.toLowerCase().includes(searchTerm.toLowerCase());
    let matchesFilter = true;
    
    switch (changeFilter) {
      case 'positive':
        matchesFilter = item.avgChange > 0;
        break;
      case 'negative':
        matchesFilter = item.avgChange < 0;
        break;
      case 'volatile':
        matchesFilter = item.volatility > 5; // High volatility threshold
        break;
      default:
        matchesFilter = true;
    }
    
    return matchesSearch && matchesFilter;
  }) || [];

  const sortedData = [...filteredData].sort((a, b) => {
    let aVal, bVal;
    switch (sortBy) {
      case 'symbol':
        aVal = a.symbol;
        bVal = b.symbol;
        break;
      case 'avgChange':
        aVal = Math.abs(a.avgChange);
        bVal = Math.abs(b.avgChange);
        break;
      case 'totalChange':
        aVal = Math.abs(a.totalChange);
        bVal = Math.abs(b.totalChange);
        break;
      case 'volatility':
        aVal = a.volatility;
        bVal = b.volatility;
        break;
      case 'volume':
        aVal = a.avgVolume;
        bVal = b.avgVolume;
        break;
      default:
        aVal = a.avgChange;
        bVal = b.avgChange;
    }
    
    if (typeof aVal === 'string') {
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  // Sort daily breakdown data
  const sortedDailyData = [...filteredData].sort((a, b) => {
    if (dailySortBy === 'symbol') {
      return dailySortDirection === 'asc' ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol);
    } else {
      // Sort by specific date
      const aRecord = a.records?.find(r => r.date === dailySortBy);
      const bRecord = b.records?.find(r => r.date === dailySortBy);
      const aVal = aRecord?.pctChange || 0;
      const bVal = bRecord?.pctChange || 0;
      
      return dailySortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const handleDailySort = (field) => {
    if (dailySortBy === field) {
      setDailySortDirection(dailySortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setDailySortBy(field);
      setDailySortDirection(field === 'symbol' ? 'asc' : 'desc');
    }
  };

  // Calculate summary stats
  const totalSymbols = dailyTracker.marketData?.length || 0;
  const avgChangeAll = totalSymbols ? 
    (dailyTracker.marketData.reduce((sum, item) => sum + item.avgChange, 0) / totalSymbols) : 0;
  const positiveCount = dailyTracker.marketData?.filter(item => item.avgChange > 0).length || 0;
  const negativeCount = dailyTracker.marketData?.filter(item => item.avgChange < 0).length || 0;
  const highVolatilityCount = dailyTracker.marketData?.filter(item => item.volatility > 5).length || 0;

  const dateColumns = dailyTracker.getDateColumns();

  const exportToCSV = () => {
    if (!dailyTracker.marketData || dailyTracker.marketData.length === 0) {
      showToast('warning', 'Export', 'No data to export');
      return;
    }

    const headers = ['Symbol', 'Avg Change %', 'Total Change %', 'Volatility', 'Avg Volume', 'Latest Price'];
    const csvContent = [
      headers.join(','),
      ...sortedData.map(item => [
        item.symbol,
        item.avgChange,
        item.totalChange,
        item.volatility,
        item.avgVolume,
        item.latestPrice
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily_changes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    showToast('success', 'Export', 'Data exported successfully');
  };

  return (
    <div className="page-section">
      {/* Configuration Panel */}
      <div style={{
        background: 'var(--secondary-dark)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '20px',
          fontSize: '16px',
          fontWeight: '600',
          color: 'var(--text-primary)'
        }}>
          <Calendar size={16} style={{ color: 'var(--accent-gold)' }} />
          Daily Change Tracker Settings
        </div>
        
        <div style={{
          display: 'flex',
          gap: '24px',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '150px' }}>
            <span style={{ fontSize: '14px', color: 'var(--text-secondary)', minWidth: '70px' }}>Days:</span>
            <select
              value={config.daysToFetch}
              onChange={(e) => handleConfigChange('daysToFetch', parseInt(e.target.value))}
              style={{
                background: 'var(--tertiary-dark)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '8px 12px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                minWidth: '80px'
              }}
            >
              <option value={3}>3 days</option>
              <option value={5}>5 days</option>
              <option value={7}>7 days</option>
              <option value={10}>10 days</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '200px' }}>
            <span style={{ fontSize: '14px', color: 'var(--text-secondary)', minWidth: '100px' }}>Min Volume:</span>
            <select
              value={config.minVolumeFilter}
              onChange={(e) => handleConfigChange('minVolumeFilter', parseInt(e.target.value))}
              style={{
                background: 'var(--tertiary-dark)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '8px 12px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                minWidth: '100px'
              }}
            >
              <option value={0}>No filter</option>
              <option value={100000}>100K</option>
              <option value={1000000}>1M</option>
              <option value={10000000}>10M</option>
              <option value={100000000}>100M</option>
            </select>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {!dailyTracker.isLoading ? (
            <button 
              onClick={handleFetchData}
              className="btn primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Play size={16} />
              Fetch Daily Data
            </button>
          ) : (
            <button 
              onClick={handleCancelFetch}
              className="btn danger"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Square size={16} />
              Cancel
            </button>
          )}
          
          <button 
            onClick={handleFetchData}
            disabled={dailyTracker.isLoading}
            className="btn secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: dailyTracker.isLoading ? 0.5 : 1
            }}
          >
            <RefreshCw size={16} />
            Refresh Data
          </button>
          
          <button 
            onClick={exportToCSV}
            disabled={!dailyTracker.marketData || dailyTracker.marketData.length === 0}
            className="btn secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: (!dailyTracker.marketData || dailyTracker.marketData.length === 0) ? 0.5 : 1
            }}
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
      }}>
        <div style={{
          background: 'var(--secondary-dark)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: 'var(--accent-gold)',
            marginBottom: '8px'
          }}>
            {totalSymbols}
          </div>
          <div style={{
            fontSize: '14px',
            color: 'var(--text-secondary)',
            marginBottom: '4px'
          }}>
            Total Symbols
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-muted)'
          }}>
            {config.daysToFetch} days analysis
          </div>
        </div>

        <div style={{
          background: 'var(--secondary-dark)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: avgChangeAll >= 0 ? 'var(--accent-green)' : 'var(--accent-red)',
            marginBottom: '8px'
          }}>
            {formatPercent(avgChangeAll)}
          </div>
          <div style={{
            fontSize: '14px',
            color: 'var(--text-secondary)',
            marginBottom: '4px'
          }}>
            Market Average
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-muted)'
          }}>
            Daily change
          </div>
        </div>

        <div style={{
          background: 'var(--secondary-dark)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: 'var(--accent-green)',
            marginBottom: '8px'
          }}>
            {positiveCount}
          </div>
          <div style={{
            fontSize: '14px',
            color: 'var(--text-secondary)',
            marginBottom: '4px'
          }}>
            Positive Movers
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-muted)'
          }}>
            vs {negativeCount} negative
          </div>
        </div>

        <div style={{
          background: 'var(--secondary-dark)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: 'var(--accent-gold)',
            marginBottom: '8px'
          }}>
            {highVolatilityCount}
          </div>
          <div style={{
            fontSize: '14px',
            color: 'var(--text-secondary)',
            marginBottom: '4px'
          }}>
            High Volatility
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-muted)'
          }}>
             5% deviation
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      <div style={{
        background: 'var(--secondary-dark)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{
          display: 'flex',
          gap: '24px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1', minWidth: '250px' }}>
            <Search size={16} style={{ color: 'var(--text-secondary)' }} />
            <span style={{ fontSize: '14px', color: 'var(--text-secondary)', minWidth: '50px' }}>Search:</span>
            <input
              type="text"
              placeholder="Enter symbol name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: 'var(--tertiary-dark)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '8px 12px',
                color: 'var(--text-primary)',
                flex: '1',
                fontSize: '14px'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={16} style={{ color: 'var(--text-secondary)' }} />
            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Filter:</span>
            <select
              value={changeFilter}
              onChange={(e) => setChangeFilter(e.target.value)}
              style={{
                background: 'var(--tertiary-dark)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '8px 12px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                minWidth: '100px'
              }}
            >
              <option value="all">All</option>
              <option value="positive">Positive</option>
              <option value="negative">Negative</option>
              <option value="volatile">High Volatility</option>
            </select>
          </div>
        </div>
      </div>

      {/* Show Loading Progress or Data */}
      {dailyTracker.isLoading ? (
        <LoadingProgress 
          isLoading={true}
          progress={dailyTracker.progress}
          message={dailyTracker.progressMessage}
        />
      ) : !dailyTracker.marketData || dailyTracker.marketData.length === 0 ? (
        <div style={{
          padding: '80px 20px',
          textAlign: 'center',
          color: 'var(--text-secondary)',
          background: 'var(--secondary-dark)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px',
            opacity: 0.5
          }}>
            📈
          </div>
          <h3 style={{
            fontSize: '18px',
            marginBottom: '8px',
            color: 'var(--text-primary)'
          }}>
            No data loaded
          </h3>
          <p style={{
            fontSize: '14px',
            color: 'var(--text-secondary)'
          }}>
            Click "Fetch Daily Data" to start analysis
          </p>
        </div>
      ) : (
        <>
          {/* Daily Breakdown Table (Now on Top) */}
          {sortedDailyData.length > 0 && dateColumns.length > 0 && (
            <div style={{
              background: 'var(--secondary-dark)',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '24px',
              boxShadow: 'var(--shadow-sm)'
            }}>
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'var(--tertiary-dark)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--text-primary)'
                }}>
                  <Calendar size={16} style={{ color: 'var(--accent-gold)' }} />
                  Daily Breakdown - Click any date to sort
                </div>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)'
                }}>
                  Last {config.daysToFetch} days performance • Showing all symbols
                </div>
              </div>
              
              <div>
                {/* Table Header for Daily Data */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `1.2fr repeat(${dateColumns.length}, 1fr)`,
                  gap: '12px',
                  padding: '16px 24px',
                  background: 'var(--primary-dark)',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  borderBottom: '1px solid var(--border-color)',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                }}>
                  <div 
                    style={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      color: dailySortBy === 'symbol' ? 'var(--accent-gold)' : 'var(--text-secondary)'
                    }}
                    onClick={() => handleDailySort('symbol')}
                  >
                    Symbol
                    {dailySortBy === 'symbol' && <span>{dailySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </div>
                  {dateColumns.map(date => (
                    <div 
                      key={date} 
                      style={{ 
                        textAlign: 'center', 
                        cursor: 'pointer',
                        color: dailySortBy === date ? 'var(--accent-gold)' : 'var(--text-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                      onClick={() => handleDailySort(date)}
                    >
                      {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      {dailySortBy === date && <span>{dailySortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  ))}
                </div>
                
                {/* Table Body for Daily Data */}
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {sortedDailyData.map((item, index) => (
                    <div
                      key={item.symbol}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `1.2fr repeat(${dateColumns.length}, 1fr)`,
                        gap: '12px',
                        padding: '16px 24px',
                        borderBottom: '1px solid var(--border-color)',
                        fontSize: '13px',
                        alignItems: 'center',
                        transition: 'all var(--transition-fast)',
                        background: item.volatility > 5 ? 'rgba(255, 193, 7, 0.05)' : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--hover-bg)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = item.volatility > 5 ? 'rgba(255, 193, 7, 0.05)' : 'transparent';
                      }}
                    >
                      <div style={{ 
                        fontWeight: '600', 
                        color: 'var(--text-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {item.symbol}
                        {item.volatility > 10 && (
                          <BarChart3 size={12} style={{ color: 'var(--accent-gold)' }} />
                        )}
                      </div>
                      
                      {dateColumns.map(date => {
                        const dayRecord = item.records?.find(r => r.date === date);
                        const change = dayRecord?.pctChange || 0;
                        return (
                          <div
                            key={date}
                            style={{
                              color: change >= 0 ? 'var(--accent-green)' : 'var(--accent-red)',
                              fontFamily: 'monospace',
                              textAlign: 'center',
                              fontWeight: Math.abs(change) > 5 ? '600' : 'normal'
                            }}
                          >
                            {dayRecord ? formatPercent(change) : '--'}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Summary Data Table */}
          <div style={{
            background: 'var(--secondary-dark)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--tertiary-dark)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--text-primary)'
              }}>
                <TrendingUp size={16} style={{ color: 'var(--accent-gold)' }} />
                Summary Analysis
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--text-secondary)'
              }}>
                Showing {sortedData.length} symbols
              </div>
            </div>
            
            <div>
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
                gap: '12px',
                padding: '16px 24px',
                background: 'var(--primary-dark)',
                fontSize: '12px',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                borderBottom: '1px solid var(--border-color)',
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}>
                <div 
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => handleSort('symbol')}
                >
                  Symbol
                  {sortBy === 'symbol' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
                <div 
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => handleSort('avgChange')}
                >
                  Avg Change
                  {sortBy === 'avgChange' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
                <div 
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => handleSort('totalChange')}
                >
                  Total Change
                  {sortBy === 'totalChange' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
                <div 
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => handleSort('volatility')}
                >
                  Volatility
                  {sortBy === 'volatility' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
                <div 
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => handleSort('volume')}
                >
                  Avg Volume
                  {sortBy === 'volume' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
                <div>Latest Price</div>
              </div>
              
              {/* Table Body */}
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {sortedData.map((item, index) => (
                  <div
                    key={item.symbol}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
                      gap: '12px',
                      padding: '16px 24px',
                      borderBottom: '1px solid var(--border-color)',
                      fontSize: '13px',
                      alignItems: 'center',
                      transition: 'all var(--transition-fast)',
                      background: item.volatility > 5 ? 'rgba(255, 193, 7, 0.05)' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--hover-bg)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = item.volatility > 5 ? 'rgba(255, 193, 7, 0.05)' : 'transparent';
                    }}
                  >
                    <div style={{ 
                      fontWeight: '600', 
                      color: 'var(--text-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {item.symbol}
                      {item.volatility > 10 && (
                        <BarChart3 size={12} style={{ color: 'var(--accent-gold)' }} />
                      )}
                    </div>
                    
                    <div style={{
                      color: item.avgChange >= 0 ? 'var(--accent-green)' : 'var(--accent-red)',
                      fontWeight: '600',
                      fontFamily: 'monospace'
                    }}>
                      {formatPercent(item.avgChange)}
                    </div>
                    
                    <div style={{
                      color: item.totalChange >= 0 ? 'var(--accent-green)' : 'var(--accent-red)',
                      fontWeight: '600',
                      fontFamily: 'monospace'
                    }}>
                      {formatPercent(item.totalChange)}
                    </div>
                    
                    <div style={{
                      color: item.volatility > 5 ? 'var(--accent-gold)' : 'var(--text-primary)',
                      fontFamily: 'monospace',
                      fontWeight: item.volatility > 5 ? '600' : 'normal'
                    }}>
                      {item.volatility.toFixed(2)}%
                    </div>
                    
                    <div style={{ 
                      color: 'var(--text-primary)',
                      fontFamily: 'monospace'
                    }}>
                      {formatNumber(item.avgVolume)}
                    </div>
                    
                    <div style={{
                      color: 'var(--text-primary)',
                      fontFamily: 'monospace'
                    }}>
                      ${item.latestPrice?.toFixed(4) || '--'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Status Footer */}
      {dailyTracker.marketData && dailyTracker.marketData.length > 0 && (
        <div style={{
          background: 'var(--secondary-dark)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: '16px 24px',
          marginTop: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: 'var(--accent-green)',
              boxShadow: '0 0 8px rgba(2, 192, 118, 0.5)'
            }}></div>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)'
            }}>
              Analysis Complete
            </span>
            <span style={{
              fontSize: '12px',
              color: 'var(--text-secondary)'
            }}>
              {dailyTracker.marketData.length} symbols analyzed
            </span>
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-secondary)'
          }}>
            Data updated: {new Date().toLocaleTimeString()}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyChangeTracker;